import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Page } from 'pages'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gender, queryClient as qc, statuses, visibleTypes } from 'keys'
import { getAssessmentDefFetch, getAssessmentFetch, saveAnswersFetch, updateAssessmentStatusFetch, uploadAudioFileFetch } from 'store/middlewares/assessment.crud'
import { useDispatch, useSelector } from 'react-redux'
import { screen_4_narrative_text_read_aloud_female, screen_4_word_list_read_aloud_female, screen_4_word_list_read_aloud_male, screen_4_narrative_text_read_aloud_male } from 'introductions'
import { _screen_4_narrative_text_read_aloud_male, _screen_4_narrative_text_read_aloud_female, _screen_4_word_list_read_aloud_male, _screen_4_word_list_read_aloud_female } from 'introductions/highGrades'
import { toast } from 'react-toastify'
import { toastConfig } from 'config'
import { app, performAssessments, practiceAssessments } from 'router/routes'
import { AbilityContext } from 'components/Permissions/Can'
import { isEmpty } from 'lodash'
import { performAssessmentHooks } from '.'
import Preview from './Preview'
import PerformAssessment from './PerformAssessment'
import MicRecorder from 'mic-recorder-to-mp3';
import ReactPlayer from 'react-player'
import { actions } from 'store'


const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const AssessmentRecordFooter = () => {
    const params = useParams()
    const { pathname, state } = useLocation()
    const navigate = useNavigate()
    const isPreviewMode = pathname.includes("preview");
    const queryClient = useQueryClient()
    const ability = useContext(AbilityContext)
    const assessmentId = params.assessmentID

    const [questionValues, setQuestionValues] = useState()
    const [readAloudFlag, setReadAloudFlag] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [audioChunks, setAudioChunks] = useState([])
    const [micDisabled, setMicDisabled] = useState(false)
    const [audioURL, setAudioURL] = useState('')
    const [isRecording, setIsRecording] = useState(false)
    const [finishFlag, setFinishFlag] = useState(false)
    const [tooltipOpen, setTooltipOpen] = useState(true)
    const [blobURL, setBlobURL] = useState('')

    const initialQueue = state?.remainingAssessmentIds || []
    const [queue, setQueue] = useState(initialQueue)

    const uploadFile = performAssessmentHooks.useUploadFile()

    const { testTaker = {} } = useSelector(state => state.testTaker)
    const isMaleGender = testTaker.gender == gender.male

    const totalAssessmentsCount =
        state?.totalAssessmentsCount ? state?.totalAssessmentsCount :
            state?.remainingAssessmentIds ? state?.remainingAssessmentIds?.length + 1 : 1

    const isFirstAssessment =
        // כשמדובר בסוללה או ברצף מבדקים אנחנו בודקים האם המבדק הוא המבדק הראשון - משמעותי לבדיקת המיקרופון שמתבצעת פעם אחת בלבד
        state?.totalAssessmentsCount == state?.remainingAssessmentIds?.length + 1
        ||
        // במקרה שמדובר בביצוע יחיד תמיד תתבצע בדיקת מיקרופון
        (!state?.totalAssessmentsCount && !state?.remainingAssessmentIds)

    const now = new Date().getTime();

    const testTakersExpiration = JSON.parse(localStorage.getItem("children-process-expired"))
    // מחלצים את הערך הספציפי למשתנה כדי שיהיה קל לעבוד איתו
    const expirationValue = testTakersExpiration?.[testTaker.test_taker_id];
    // האם הסשן פג תוקף? 
    // (רק אם המידע קיים נבדוק זמן, אם הוא לא קיים נניח שפג תוקף - אבל רק אחרי הטעינה)
    const sessionExpired = expirationValue
        ? now > parseInt(expirationValue)
        : true

    const assessmentData = useQuery({
        queryKey: [qc.performAssessment, assessmentId],
        queryFn: async ({ signal }) => {
            const { data } = await getAssessmentFetch({ id: assessmentId, signal, })
            return data
        }
    })
    const assessment = assessmentData?.data || {}
    const assessments = assessmentData?.data ? [assessmentData.data] : []

    const assessmentDefData = useQuery({
        queryKey: [qc.assessmentDef, assessmentId, assessment?.reading_assessment_def_id],
        queryFn: async ({ signal }) => {
            const { data } = await getAssessmentDefFetch({ id: assessment?.reading_assessment_def_id, signal, })
            return data
        },
        enabled: !!assessment?.reading_assessment_def_id
    })
    const assessmentDef = assessmentDefData?.data || {}

    const dispatch = useDispatch()

    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const animationIdRef = useRef(null);
    const assessmentRef = useRef(assessment)
    const assessmentsRef = useRef(assessments)
    const isMountedRef = useRef(true)

    // מתחיל מכיתה ה
    const isHighGrade = assessment?.reading_assessment_grade_of_execution >= 8

    const { mutateAsync: updateAssessmentStatus } = useMutation({
        mutationFn: updateAssessmentStatusFetch
    })

    const { mutateAsync: saveAnswerChoices } = useMutation({
        mutationFn: saveAnswersFetch
    })

    // מעבר למבדק הבא בתור (תמיד משתמש במצב עדכני של queue)
    const goToNextAssessmentInQueue = () => {
        let moved = false

        let returnToPath = app.performAssessments
        const section = pathname.match(/^\/([^/]+)/)?.[1];

        let basePath = app.performAssessments
        if (section == practiceAssessments)
            basePath = app.practiceAssessments

        setQueue(prevQueue => {
            const [nextId, ...rest] = prevQueue

            navigate(`/between/${nextId || "complete"}`, {
                state: {
                    basePath: basePath,
                    totalAssessmentsCount: totalAssessmentsCount,
                    remainingAssessmentIds: rest,
                    isMaleGender: isMaleGender,
                    testTakerName: testTaker.first_name,
                    isHighGrade: isHighGrade,
                    completedAssessmentsCount: state?.assessmentPerformStep || 1,
                }
            })

            moved = true
            return rest
        })

        return moved
    }

    const saveAnswers = useCallback(() => {
        let answers = []
        const asessment = queryClient.getQueryData([qc.assessmentDef, assessmentId, assessmentDefData?.data?.id?.toString()])
        asessment.questions.forEach((question, index) => {
            const answerIndex = questionValues[index];
            const answerDef = question.answer_defs[answerIndex];
            answers.push({ answer_def: answerDef.id })
        });
        saveAnswerChoices({ id: assessment.id, answers: answers })
    }, [questionValues, assessment, assessmentId, assessmentDefData, saveAnswerChoices, queryClient])

    const draw = (analyser) => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        analyser.getByteFrequencyData(dataArray);

        if (context) {
            context.fillStyle = 'rgba(255, 255, 255, 0.3)';
            context.fillRect(0, 0, canvas.width, canvas.height);

            const barWidth = (canvas.width / bufferLength) * 2.5;
            let barHeight;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                barHeight = dataArray[i] / 6;
                context.fillStyle = 'gray';
                context.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
                x += barWidth + 1;
            }

            animationIdRef.current = requestAnimationFrame(() => draw(analyser));
        }
    };

    const checkVolume = (analyser) => {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteTimeDomainData(dataArray)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
            let normalized = (dataArray[i] - 128) / 128;
            sum += normalized * normalized;
        }
        let volume = Math.sqrt(sum / dataArray.length)
        console.log("volume", volume)

        return volume
    }

    const releaseStream = useCallback(() => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks?.().forEach((track) => track.stop())
            mediaStreamRef.current = null
        }
        if (animationIdRef.current != null) {
            cancelAnimationFrame(animationIdRef.current)
            animationIdRef.current = null
        }
    }, [])

    const startRecord = useCallback(() => {
        if (micDisabled || isRecording) return
        if (!isMountedRef.current) return

        Mp3Recorder
            .start()
            .then((stream) => {
                if (!isMountedRef.current) {
                    stream?.getTracks?.().forEach((track) => track.stop())
                    return
                }

                setIsRecording(true)
                mediaStreamRef.current = stream

                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)()
                analyserRef.current = audioContextRef.current.createAnalyser()
                const source = audioContextRef.current.createMediaStreamSource(stream)
                source.connect(analyserRef.current)
                analyserRef.current.fftSize = 2048

                draw(analyserRef.current)

                let count = 0;
                let volumes = []
                const interval = setInterval(() => {
                    if (count < 4) {
                        const result = checkVolume(analyserRef.current)
                        volumes.push(result)
                        count += 1;
                    } else {
                        clearInterval(interval);
                        const allLessThanPointOne = volumes.every(value => value < 0.015);
                        if (allLessThanPointOne) {
                            toast("הרמקול שלך לא עובד...", toastConfig)
                        }
                    }
                }, 1000);
            })
            .catch((e) => console.error(e));
    }, [micDisabled, isRecording])

    const stopRecord = useCallback((successFunc) => {
        assessmentRef.current = assessment
        assessmentsRef.current = assessments

        const stopAndRelease = () => {
            try {
                Mp3Recorder?.stop()
            } catch (e) {
                console.log(e)
            }
            releaseStream()
            setIsRecording(false)
        }

        return Mp3Recorder
            ?.stop()
            ?.getMp3()
            ?.then(([buffer, blob]) => {
                const nAudioChunks = [...audioChunks, blob]
                setAudioChunks(nAudioChunks)
                setIsRecording(false)

                const audioBlob = new Blob(nAudioChunks, { type: 'audio/mp3' })
                setBlobURL(audioBlob)

                if (isPreviewMode) {
                    const audioFile = URL.createObjectURL(audioBlob)
                    setBlobURL(audioFile)
                } else {
                    successFunc?.(audioBlob)
                }
            })
            .catch((e) => {
                console.log(e)
                stopAndRelease()
            }) ?? Promise.resolve()
    }, [assessments, assessment, audioChunks, isPreviewMode, releaseStream])

    const successFunc = useCallback((audioBlob) => {
        uploadFile(assessmentRef.current, audioBlob)
    }, [updateAssessmentStatus, uploadFile, queryClient])

    const assessmentDefItem = {
        [visibleTypes.PARAGRAPH]: {
            record:
                isHighGrade ?
                    (isMaleGender ? _screen_4_narrative_text_read_aloud_male : _screen_4_narrative_text_read_aloud_male) :
                    (isMaleGender ? screen_4_narrative_text_read_aloud_male : screen_4_narrative_text_read_aloud_female),
            onSuccess: () => stopRecord(successFunc)
        },
        [visibleTypes.WORD_GROUP]: {
            record:
                isHighGrade ?
                    (isMaleGender ? _screen_4_word_list_read_aloud_male : _screen_4_word_list_read_aloud_female) :
                    (isMaleGender ? screen_4_word_list_read_aloud_male : screen_4_word_list_read_aloud_female),
            onSuccess: () => stopRecord(successFunc)
        },
        [visibleTypes.MULTIPLE_CHOICE]: {
            onSuccess: saveAnswers
        }
    }
    const item = assessmentDefItem?.[assessmentDef?.visible_type]
    const isQuestionAssessment = assessmentDef?.visible_type == visibleTypes.MULTIPLE_CHOICE

    const saveAssessment = useCallback(() => {
        if (!item?.onSuccess) return Promise.resolve()

        const result = item.onSuccess()
        return result && typeof result.then === 'function' ? result : Promise.resolve()
    }, [item])

    useEffect(() => {
        dispatch(actions.setIsInAssessmentMode(true))
        isMountedRef.current = true

        return () => {
            isMountedRef.current = false

            try {
                Mp3Recorder?.stop()
            } catch (e) {
                console.log(e)
            }

            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks?.().forEach((track) => track.stop())
                mediaStreamRef.current = null
            }

            if (animationIdRef.current != null) {
                cancelAnimationFrame(animationIdRef.current)
                animationIdRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        const handlePopState = () => {
            try {
                Mp3Recorder?.stop()
            } catch (e) {
                console.log(e)
            }

            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks?.().forEach((track) => track.stop())
                mediaStreamRef.current = null
            }

            if (animationIdRef.current != null) {
                cancelAnimationFrame(animationIdRef.current)
                animationIdRef.current = null
            }
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])

    useEffect(() => {
        let timer
        if (isHighGrade && readAloudFlag) {
            timer = setTimeout(startRecord, 2000);
        }
        return () => clearTimeout(timer);
    }, [isHighGrade, readAloudFlag])

    const isLoading = isEmpty(testTaker) || isEmpty(assessment) || isEmpty(assessmentDef);

    const commonValue = {
        assessments,
        assessment,
        currentStep, setCurrentStep,
        readAloudFlag, setReadAloudFlag,
        assessmentDef,
        isQuestionAssessment,
        goToNextAssessmentInQueue,
        micDisabled, setMicDisabled,
        audioChunks, setAudioChunks,
        audioURL, setAudioURL,
        blobURL,
        isHighGrade,
        Mp3Recorder,
        finishFlag, setFinishFlag,
        questionValues, setQuestionValues,
        isMaleGender,
        isRecording, setIsRecording,
        tooltipOpen, setTooltipOpen,
        startRecord,
        stopRecord,
        saveAssessment,
        sessionExpired,
        canvasRef,
        isPreviewMode,
        isLoading,
        queue, setQueue,
        isFirstAssessment,
        showNextButton: ability.can("manage", "super-user") || isHighGrade
    }

    return (
        <Page>
            <PerformAssessmentContext.Provider value={commonValue}>
                <PerformAssessment />
            </PerformAssessmentContext.Provider>
            <ReactPlayer
                playing={readAloudFlag}
                url={item?.record}
                width='0px' height='0px'
                onEnded={startRecord}
            />
            {!isPreviewMode || <Preview />}
        </Page>
    )
}

export const PerformAssessmentContext = createContext()
export default AssessmentRecordFooter